import * as THREE from "three";

/**
 * Client-side ground rendering for MuJoCo plane geoms.
 *
 * The packed scene declares a bare `us_scene_ground` plane whose flat colour blends into the
 * clear colour, so objects appear to float. This module renders such planes as a metre-scaled
 * grid with a radial distance fade, plus a soft contact shadow under the object. It is purely
 * presentational: the plane geom, its collision flags and the physics are untouched.
 *
 * Textures are generated once on a 2D canvas and uploaded once per viewer; nothing here does
 * per-frame texture work.
 */

/** Renderer clear colour; the far ground fades into it so no horizon line cuts across the view. */
export const GROUND_CLEAR_COLOR = 0xe8ecee;
// Ground tones sit a hair below the clear colour so the near floor still reads as a surface.
const TILE_BASE = "#e1e5e7";
const MINOR_LINE = "#d4dadd";
const MAJOR_LINE = "#b3bbbf";
const TILE_PX = 512;
const MINOR_CELLS_PER_METRE = 10;
const FADE_PX = 256;
const SHADOW_PX = 256;
const SHADOW_ALPHA = 0.34;
/** Ground half-extent as a multiple of the object's bounding radius, clamped to metres. */
const EXTENT_PER_RADIUS = 16;
const MIN_HALF_EXTENT = 4;
const MAX_HALF_EXTENT = 60;

let tileCanvas: HTMLCanvasElement | null = null;
let fadeCanvas: HTMLCanvasElement | null = null;

function context2d(width: number, height: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("2D canvas is unavailable for ground textures");
  return [canvas, context];
}

/** One square metre: 10 cm minor lines, 1 m major lines on the tile edges so the repeat is seamless. */
function gridTile(): HTMLCanvasElement {
  if (tileCanvas) return tileCanvas;
  const [canvas, context] = context2d(TILE_PX, TILE_PX);
  context.fillStyle = TILE_BASE;
  context.fillRect(0, 0, TILE_PX, TILE_PX);
  const cell = TILE_PX / MINOR_CELLS_PER_METRE;
  context.fillStyle = MINOR_LINE;
  for (let index = 1; index < MINOR_CELLS_PER_METRE; index += 1) {
    const at = index * cell;
    context.fillRect(at - 1, 0, 2, TILE_PX);
    context.fillRect(0, at - 1, TILE_PX, 2);
  }
  context.fillStyle = MAJOR_LINE;
  context.fillRect(0, 0, 2, TILE_PX);
  context.fillRect(TILE_PX - 2, 0, 2, TILE_PX);
  context.fillRect(0, 0, TILE_PX, 2);
  context.fillRect(0, TILE_PX - 2, TILE_PX, 2);
  tileCanvas = canvas;
  return canvas;
}

/** Radial alpha: opaque out to 45% of the half-extent, gone by 95%, so the square edge never shows. */
function fadeMask(): HTMLCanvasElement {
  if (fadeCanvas) return fadeCanvas;
  const [canvas, context] = context2d(FADE_PX, FADE_PX);
  const half = FADE_PX / 2;
  const gradient = context.createRadialGradient(half, half, 0, half, half, half);
  gradient.addColorStop(0, "#fff");
  gradient.addColorStop(0.45, "#fff");
  gradient.addColorStop(0.7, "#999");
  gradient.addColorStop(0.95, "#000");
  gradient.addColorStop(1, "#000");
  context.fillStyle = gradient;
  context.fillRect(0, 0, FADE_PX, FADE_PX);
  fadeCanvas = canvas;
  return canvas;
}

/**
 * Soft contact shadow: the object's footprint (a rounded rectangle, `spanX` by `spanZ` metres) at
 * full alpha, falling off over `margin` metres on every side. The sprite covers `width` by `depth`
 * metres, so the distance field is evaluated in world units and the falloff stays uniform even
 * when the footprint is far from square.
 */
function shadowSprite(width: number, depth: number, spanX: number, spanZ: number, margin: number): HTMLCanvasElement {
  const [canvas, context] = context2d(SHADOW_PX, SHADOW_PX);
  const image = context.createImageData(SHADOW_PX, SHADOW_PX);
  const halfX = spanX / 2;
  const halfZ = spanZ / 2;
  const corner = Math.min(halfX, halfZ) * 0.35;
  for (let row = 0; row < SHADOW_PX; row += 1) {
    const pz = ((row + 0.5) / SHADOW_PX - 0.5) * depth;
    for (let column = 0; column < SHADOW_PX; column += 1) {
      const px = ((column + 0.5) / SHADOW_PX - 0.5) * width;
      // Signed distance to a rounded rectangle centred on the sprite.
      const dx = Math.abs(px) - (halfX - corner);
      const dz = Math.abs(pz) - (halfZ - corner);
      const outside = Math.hypot(Math.max(dx, 0), Math.max(dz, 0)) + Math.min(Math.max(dx, dz), 0) - corner;
      const t = THREE.MathUtils.clamp(outside / margin, 0, 1);
      const alpha = SHADOW_ALPHA * (1 - t) * (1 - t) * (1 + 0.6 * t);
      image.data[(row * SHADOW_PX + column) * 4 + 3] = Math.round(alpha * 255);
    }
  }
  context.putImageData(image, 0, 0);
  return canvas;
}

export interface GroundFootprint {
  /** Object bounds in three.js world coordinates at rest. */
  min: THREE.Vector3;
  max: THREE.Vector3;
}

export interface GroundVisual {
  /** Meshes to add to the scene (the plane and, when a footprint is known, the contact shadow). */
  objects: THREE.Object3D[];
  /**
   * Move the contact shadow with the object's root body: `dx`/`dz` are the root's translation since
   * rest (three.js frame), `yaw` its rotation about the vertical since rest, `lift` metres above rest.
   */
  follow(dx: number, dz: number, yaw: number, lift: number): void;
  dispose(): void;
}

export interface GroundOptions {
  renderer: THREE.WebGLRenderer;
  /** Half-extents from the plane geom; zero means "infinite", sized from the object instead. */
  planeHalfSize: [number, number];
  /** Bounding radius of the object, used to size an infinite plane and the fade. */
  objectRadius: number;
  /** The object's resting bounds, or null when the scene has nothing to shadow. */
  footprint: GroundFootprint | null;
  /** Ground height in three.js world units (the plane's world Y). */
  groundY: number;
  /** Rest position (three.js frame) of the body the shadow follows; the shadow orbits it on yaw. */
  anchor: THREE.Vector3 | null;
}

/**
 * Build the plane geometry (in MuJoCo-local XY, +Z normal, like `primitiveGeometry`) and the
 * contact shadow. The caller places the plane with the geom matrix as it does for any geom; the
 * shadow is world-placed by this module.
 */
export function buildGround(options: GroundOptions): { plane: THREE.Mesh; visual: GroundVisual } {
  const [sizeX, sizeY] = options.planeHalfSize;
  const infinite = sizeX <= 0.01 || sizeY <= 0.01;
  const sized = Math.ceil(THREE.MathUtils.clamp(options.objectRadius * EXTENT_PER_RADIUS, MIN_HALF_EXTENT, MAX_HALF_EXTENT));
  const halfX = infinite ? sized : sizeX;
  const halfY = infinite ? sized : sizeY;
  const anisotropy = Math.min(8, options.renderer.capabilities.getMaxAnisotropy());

  const tile = new THREE.CanvasTexture(gridTile());
  tile.wrapS = THREE.RepeatWrapping;
  tile.wrapT = THREE.RepeatWrapping;
  tile.repeat.set(halfX * 2, halfY * 2);
  tile.anisotropy = anisotropy;
  tile.colorSpace = THREE.SRGBColorSpace;
  const fade = new THREE.CanvasTexture(fadeMask());
  fade.colorSpace = THREE.NoColorSpace;
  const planeMaterial = new THREE.MeshBasicMaterial({
    map: tile,
    alphaMap: fade,
    transparent: true,
    depthWrite: true,
    // Push the ground back a hair so the contact shadow decal never z-fights it.
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 2,
  });
  const planeGeometry = new THREE.PlaneGeometry(halfX * 2, halfY * 2);
  const plane = new THREE.Mesh(planeGeometry, planeMaterial);
  plane.matrixAutoUpdate = false;
  plane.frustumCulled = false;
  // Drawn first among transparent objects so translucent parts never get painted over.
  plane.renderOrder = -2;

  const objects: THREE.Object3D[] = [plane];
  let shadow: THREE.Mesh | null = null;
  let shadowTexture: THREE.CanvasTexture | null = null;
  let shadowMaterial: THREE.MeshBasicMaterial | null = null;
  let shadowGeometry: THREE.PlaneGeometry | null = null;
  const base = new THREE.Vector3();
  const anchor = options.anchor?.clone() ?? null;
  let fadeHeight = 1;
  if (options.footprint) {
    const { min, max } = options.footprint;
    const spanX = Math.max(0.02, max.x - min.x);
    const spanZ = Math.max(0.02, max.z - min.z);
    const largest = Math.max(spanX, spanZ);
    const margin = THREE.MathUtils.clamp(largest * 0.22, 0.015, 0.35);
    const width = spanX + margin * 2;
    const depth = spanZ + margin * 2;
    shadowTexture = new THREE.CanvasTexture(shadowSprite(width, depth, spanX, spanZ, margin));
    shadowTexture.colorSpace = THREE.NoColorSpace;
    shadowMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      map: shadowTexture,
      transparent: true,
      depthWrite: false,
    });
    shadowGeometry = new THREE.PlaneGeometry(width, depth);
    shadowGeometry.rotateX(-Math.PI / 2);
    shadow = new THREE.Mesh(shadowGeometry, shadowMaterial);
    shadow.renderOrder = -1;
    shadow.frustumCulled = false;
    base.set((min.x + max.x) / 2, options.groundY + Math.max(0.0005, largest * 0.0015), (min.z + max.z) / 2);
    shadow.position.copy(base);
    fadeHeight = Math.max(0.05, largest * 0.6);
    objects.push(shadow);
  }

  const visual: GroundVisual = {
    objects,
    follow(dx, dz, yaw, lift) {
      if (!shadow || !shadowMaterial) return;
      if (anchor) {
        // Rotate the footprint centre about the root body, then translate with it.
        const ox = base.x - anchor.x;
        const oz = base.z - anchor.z;
        const c = Math.cos(yaw);
        const sn = Math.sin(yaw);
        shadow.position.set(anchor.x + dx + c * ox + sn * oz, base.y, anchor.z + dz - sn * ox + c * oz);
        shadow.rotation.y = yaw;
      } else {
        shadow.position.set(base.x + dx, base.y, base.z + dz);
      }
      shadowMaterial.opacity = THREE.MathUtils.clamp(1 - lift / fadeHeight, 0, 1);
    },
    dispose() {
      tile.dispose();
      fade.dispose();
      planeMaterial.dispose();
      planeGeometry.dispose();
      shadowTexture?.dispose();
      shadowMaterial?.dispose();
      shadowGeometry?.dispose();
    },
  };
  return { plane, visual };
}
